import * as React from 'react';

import { FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Spinner from 'components/spinner/Spinner';

import { __t, applyTranslations } from 'utils/language';
import { isGreaterThanZero, isIntegerMoneySum, isNotEmpty, isRequired } from 'utils/validators';

import './campaign-payouts-variable-payments-modal-fields.scss';

import type { SubcampaignInfluencerProfile, TextEntry, VariablePayment } from 'types';

import type { SyntheticEvent } from 'react';

applyTranslations(require('./campaign-payouts-variable-payments-modal-fields.i18n.json'));

type Props = {
  variablePayments: Array<VariablePayment>,
  influencerProfile: SubcampaignInfluencerProfile,
  isLoading: boolean,
  onFieldRemove: (a: number) => void,
  onFieldChange: (event: SyntheticEvent<TextEntry>) => void,
  onFieldFocus: (event: SyntheticEvent<TextEntry>) => void
};

const msgKey = 'campaign_payouts_variable_payments_modal_fields';

class CampaignPayoutsVariablePaymentsModalFields extends React.PureComponent<Props> {
  handleFieldRemove = (event: SyntheticEvent<HTMLElement>): void => {
    const { onFieldRemove } = this.props;
    const target = event.currentTarget;
    const id = Number(target.dataset.id) || 0;

    onFieldRemove(id);
  };

  renderHeader(): React.ReactNode {
    return (
      <div className="campaign-payouts-variable-payments-modal-fields__header">
        <div className="campaign-payouts-variable-payments-modal-fields__header-item">
          {__t(`${msgKey}.labels.description`)}
        </div>
        <div className="campaign-payouts-variable-payments-modal-fields__header-item">
          {__t(`${msgKey}.labels.amount`)}
        </div>
      </div>
    );
  }

  renderCross(id: number): React.ReactNode {
    return (
      <div className="campaign-payouts-variable-payments-modal-fields__field-cross">
        <SVGDynamicIcon
          name="cross-custom-fg"
          width={8}
          height={8}
          customColor="grey"
          clickable
          onClick={this.handleFieldRemove}
          data={{ id }}
        />
      </div>
    );
  }

  renderTextField(value: string, id: number): React.ReactNode {
    const { onFieldChange } = this.props;

    return (
      <div className="campaign-payouts-variable-payments-modal-fields__field-item">
        <FormField
          direction="column"
          showValidationIcon={false}
          validators={[
            isNotEmpty(value),
            isRequired(value)
          ]}
        >
          <Input
            type="text"
            name="description"
            width="responsive"
            value={value}
            maxLength={100}
            data={{ id }}
            placeholder={__t(`${msgKey}.placeholders.commission`)}
            className="campaign-payouts-variable-payments-modal-fields__input"
            onChange={onFieldChange}
          />
        </FormField>
      </div>
    );
  }

  renderNumberField(value: number, id: number): React.ReactNode {
    const { onFieldChange, onFieldFocus } = this.props;
    const isIntegerBudgetValidation = isIntegerMoneySum(value);

    return (
      <div className="campaign-payouts-variable-payments-modal-fields__field-item">
        <div className="campaign-payouts-variable-payments-modal-fields__field-item">
          <FormField
            direction="column"
            showValidationIcon={false}
            validators={[
              isGreaterThanZero(value),
              isIntegerMoneySum(value)
            ]}
            immediateValidationError={(isIntegerBudgetValidation.valid) ? '' : isIntegerBudgetValidation.message}
          >
            <Input
              type="number"
              name="amount"
              min={0}
              step={1}
              width="responsive"
              value={String(value)}
              data={{ id }}
              className="campaign-payouts-variable-payments-modal-fields__number"
              onChange={onFieldChange}
              onFocus={onFieldFocus}
            />
          </FormField>
        </div>
      </div>
    );
  }

  renderField = (item: VariablePayment): React.ReactNode | undefined | null => {
    const { influencerProfile } = this.props;
    const id = Number(item.id) || 0;

    return !item.isDeleted && (
      <React.Fragment
        key={`campaign-payouts-variable-payments-modal-fields__field-${influencerProfile.id}-${id}`}
      >
        {this.renderTextField(item.description, id)}
        {this.renderNumberField(item.amount, id)}
        {this.renderCross(id)}
      </React.Fragment>
    );
  };

  renderFields(): React.ReactNode {
    const { variablePayments } = this.props;

    return (
      <div className="campaign-payouts-variable-payments-modal-fields__fields">
        {
          variablePayments.map(this.renderField)
        }
      </div>
    );
  }

  renderSpinner(): React.ReactNode {
    return (
      <div className="campaign-payouts-variable-payments-modal-fields__spinner">
        <Spinner
          position="inline"
          size="small"
          color="light"
        />
      </div>
    );
  }

  render(): React.ReactNode {
    const { isLoading } = this.props;

    return (
      <div className="campaign-payouts-variable-payments-modal-fields">
        {this.renderHeader()}
        {
          isLoading
            ? this.renderSpinner()
            : this.renderFields()
        }
      </div>
    );
  }
}

export default CampaignPayoutsVariablePaymentsModalFields;
